import { Box, IconButton, Tooltip } from '@mui/material';
import type { GridColDef, DataGridProProps } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { QUERY_KEYS } from '../../constants';
import API_ROUTES from '../../constants/apiRoutes';
import { defaultColumnsCampaigns } from '../../helpers/table';
import useMutateData from '../../hooks/useMutateData';
import { usePagination } from '../../hooks/usePagination';
import useSort from '../../hooks/useSort';
import { createColumn, formatSnakeCaseString, copyToClipboard } from '../../utils';
import TableTemplate from '../templates/TableTemplate';
import { createEditColumn, createNumberColumn } from '../../helpers/columns';
import { AffiliateCampaign } from '../../@types/api';
import { ContentCopy, GroupOutlined, Warning } from '@mui/icons-material';
import { useToast } from '../../contexts/ToastContext';
import { AFFILIATE_CAMPAIGN_STATUS } from '../../constants/enums';
import { ResponsiveTooltip } from '../atoms/ResponsiveTooltip';
import usePermissions from '../../hooks/usePermissions';
import { useNavigate } from 'react-router';

const styles = {
  container: { height: '100%', display: 'flex', flexDirection: 'column' },
  table: { flex: 1 },
  statusWarningIcon: { ml: 1, cursor: 'pointer' },
};

const CampaignsPage = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useToast();

  const { isAdmin, isSuperAdmin } = usePermissions();
  const canManageCampaigns = !isAdmin && !isSuperAdmin;

  const { data, updateQueryParams, isFetching, isLoading, changeQuery } = usePagination<AffiliateCampaign>(
    API_ROUTES.CAMPAIGNS,
    {
      page: 1,
      limit: 25,
    },
    undefined,
    'crm'
  );

  const { deleteData } = useMutateData(API_ROUTES.CAMPAIGNS, [QUERY_KEYS.campaigns], 'crm');
  const { handleSort } = useSort(changeQuery);

  const handleSearch = (value: string | null) => {
    changeQuery({ search: value });
  };

  const columns: GridColDef[] = useMemo(
    () => [
      createColumn('id', 'ID', { sortable: false }),
      createColumn('name', 'Name'),
      createColumn('affiliate', 'Affiliate', {
        sortable: false,
        valueGetter: (_value, row) => row.affiliate?.fullName,
      }),
      createColumn('type', 'Type', { valueFormatter: (value) => formatSnakeCaseString(value) }),
      createColumn('code', 'Promo Code'),
      createColumn('product', 'Product', {
        valueFormatter: (value) => formatSnakeCaseString(value),
      }),
      createNumberColumn('fixedAmount', 'Bonus Amount', {
        valueGetter: (_value, row) => row.campaignBonus?.fixedAmount,
        hideZeros: true,
      }),
      createNumberColumn('bonusPercentage', 'Bonus (%)', {
        valueGetter: (_value, row) => row.campaignBonus?.bonusPercentage,
        hideZeros: true,
      }),
      createNumberColumn('capLimit', 'Cap Limit', {
        valueGetter: (_value, row) => row.campaignBonus?.capLimit,
      }),
      createNumberColumn('minDepositAmount', 'Min Deposit', {
        valueGetter: (_value, row) => row.campaignBonus?.minDepositAmount,
      }),
      createColumn('timeframe', 'Timeframe (days)', {
        valueGetter: (_value, row) => row.campaignBonus?.timeframe,
      }),
      createNumberColumn('sbWagering', 'SB Wagering', {
        valueGetter: (_value, row) => row.campaignBonus?.sbWagering,
      }),
      createNumberColumn('casinoWagering', 'Casino Wagering', {
        valueGetter: (_value, row) => row.campaignBonus?.casinoWagering,
      }),
      createColumn('status', 'Status', {
        renderCell: (params) => (
          <>
            {formatSnakeCaseString(params.row.status)}
            {params.row.status === AFFILIATE_CAMPAIGN_STATUS.READY && (
              <ResponsiveTooltip content='Campaign is inactive until switched to In Progress'>
                <Warning fontSize='small' color='warning' sx={styles.statusWarningIcon} />
              </ResponsiveTooltip>
            )}
          </>
        ),
      }),
      createColumn('description', 'Description', { sortable: false }),
      createEditColumn<AffiliateCampaign>({
        deleteData: canManageCampaigns ? deleteData : undefined,
        columnName: 'Actions',
        extraActions: (params) => (
          <>
            <Tooltip title='View players'>
              <IconButton
                color='primary'
                onClick={() => navigate(`/crm/players?campaignId=${encodeURIComponent(params.row.id)}`)}
                size='small'
              >
                <GroupOutlined fontSize='small' />
              </IconButton>
            </Tooltip>
            {params.row.url && (
              <Tooltip title='Copy URL'>
                <IconButton
                  color='primary'
                  onClick={() => {
                    copyToClipboard(params.row.url);
                    enqueueSnackbar('URL copied to clipboard', { variant: 'success' });
                  }}
                  size='small'
                >
                  <ContentCopy fontSize='small' />
                </IconButton>
              </Tooltip>
            )}
          </>
        ),
      }),
    ],
    [canManageCampaigns, deleteData, enqueueSnackbar, navigate]
  );

  return (
    <Box sx={styles.container}>
      <TableTemplate
        rows={data?.items || []}
        columns={columns}
        loading={isFetching || isLoading}
        rowCount={data?.count || 0}
        defaultVisibleColumns={defaultColumnsCampaigns}
        handlePaginationModelChange={updateQueryParams}
        handleSearch={handleSearch}
        handleSort={handleSort}
        changeQuery={changeQuery}
        formType={canManageCampaigns ? 'campaigns' : undefined}
        createItemBtnLabel={canManageCampaigns ? 'Add new campaign' : undefined}
        sx={styles.table as DataGridProProps['sx']}
        showExport
      />
    </Box>
  );
};

export default CampaignsPage;
